/**
 * FederationManager — admin UI for managing inter-organisation federation agreements.
 *
 * Features:
 *  - Table of all agreements (active, pending, terminated)
 *  - Invite new partner organisation modal
 *  - Accept / decline incoming invitations
 *  - Terminate existing agreements
 *  - Toggle per-agreement permissions (rooms, discovery, files, calls)
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authHeaders, errorMessage, showAdminError } from './adminApi';
import { uiLang } from './i18nUtil';

type FedStatus = 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'TERMINATED';

interface FederationAgreement {
  id:                   string;
  initiating_org_id:    string;
  partner_org_id:       string;
  status:               FedStatus;
  allow_rooms:          boolean;
  allow_discovery:      boolean;
  allow_files:          boolean;
  allow_calls:          boolean;
  max_classification:   string;
  initiated_at:         string;
  activated_at?:        string;
}

const STATUS_STYLES: Record<FedStatus, string> = {
  PENDING:    'bg-nord-13/20 text-nord-13',
  ACTIVE:     'bg-nord-14/20 text-nord-14',
  SUSPENDED:  'bg-nord-11/20 text-nord-11',
  TERMINATED: 'bg-nord-2 text-nord-muted',
};

async function apiFetch<T>(url: string, opts?: RequestInit): Promise<T> {
  // session token + Content-Type only with a body (see adminApi.ts); failed actions are shown
  const headers: Record<string, string> = { ...authHeaders(), ...(opts?.body != null ? { 'Content-Type': 'application/json' } : {}), ...(opts?.headers as Record<string, string> | undefined) };
  const res = await fetch(url, { ...opts, headers });
  if (!res.ok) {
    const msg = await errorMessage(res);
    if ((opts?.method ?? 'GET') !== 'GET') showAdminError(msg);
    throw new Error(msg);
  }
  return (await res.json() as { data: T }).data;
}

export function FederationManager() {
  const { t, i18n } = useTranslation();
  const qc    = useQueryClient();

  const [showInvite, setShowInvite] = useState(false);
  const [partnerOrgId, setPartnerOrgId] = useState('');
  const [permissions, setPermissions] = useState({
    allowRooms: true, allowDiscovery: true, allowFiles: false, allowCalls: false,
  });
  const [inviteError, setInviteError] = useState<string | null>(null);

  const agreementsQ = useQuery({
    queryKey: ['federation'],
    queryFn:  () => apiFetch<{ agreements: FederationAgreement[] }>('/v1/federation'),
  });

  const inviteMutation = useMutation({
    mutationFn: (body: unknown) =>
      apiFetch('/v1/federation/invite', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['federation'] });
      setShowInvite(false);
      setPartnerOrgId('');
      setInviteError(null);
    },
    onError: (err: Error) => setInviteError(err.message),
  });

  const acceptMutation = useMutation({
    mutationFn: (id: string) => apiFetch(`/v1/federation/${id}/accept`, { method: 'POST' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['federation'] }),
  });

  const declineMutation = useMutation({
    mutationFn: (id: string) => apiFetch(`/v1/federation/${id}/decline`, { method: 'POST' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['federation'] }),
  });

  const terminateMutation = useMutation({
    mutationFn: (id: string) => apiFetch(`/v1/federation/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['federation'] }),
  });

  const togglePermission = (id: string, perm: string, current: boolean) => {
    // the API takes camelCase (allow_rooms -> allowRooms); snake_case was silently ignored
    const field = perm.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
    void apiFetch(`/v1/federation/${id}/permissions`, {
      method: 'PATCH',
      body:   JSON.stringify({ [field]: !current }),
    }).then(() => qc.invalidateQueries({ queryKey: ['federation'] })).catch(() => { /* shown by apiFetch */ });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-nord-4">{t('adminUi.federation.title', 'Federation')}</h2>
        <button
          onClick={() => setShowInvite(!showInvite)}
          className="rounded-lg bg-nord-frost-1 px-4 py-2 text-sm font-semibold text-nord-0 hover:bg-nord-frost-2"
        >
          {t('adminUi.federation.invite', '+ Invite Organisation')}
        </button>
      </div>

      {/* Invite form */}
      {showInvite && (
        <div className="rounded-lg border border-nord-2 bg-nord-1 p-5 flex flex-col gap-3">
          <h3 className="text-sm font-semibold text-nord-4">{t('adminUi.federation.newInvite', 'Invite Partner Organisation')}</h3>
          <input
            type="text"
            value={partnerOrgId}
            onChange={e => setPartnerOrgId(e.target.value)}
            placeholder={t('adminUi.federation.partnerOrgId', 'Partner Organisation ID (UUID)')}
            className="rounded-lg border border-nord-3 bg-nord-0 px-3 py-2 text-sm text-nord-4 placeholder-nord-muted focus:border-nord-frost-1 focus:outline-none"
          />
          <div className="grid grid-cols-2 gap-2 text-sm">
            {[
              ['allowRooms',     t('adminUi.federation.rooms',     'Shared Rooms')],
              ['allowDiscovery', t('adminUi.federation.discovery', 'User Discovery')],
              ['allowFiles',     t('adminUi.federation.files',     'File Sharing')],
              ['allowCalls',     t('adminUi.federation.calls',     'Voice / Video Calls')],
            ].map(([key, label]) => (
              <label key={key as string} className="flex items-center gap-2 cursor-pointer text-nord-muted hover:text-nord-4">
                <input
                  type="checkbox"
                  checked={permissions[key as keyof typeof permissions]}
                  onChange={(e) => setPermissions(p => ({ ...p, [key as string]: e.target.checked }))}
                  className="rounded"
                />
                {label}
              </label>
            ))}
          </div>
          {inviteError && <p className="text-sm text-nord-11">{inviteError}</p>}
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowInvite(false)} className="text-sm text-nord-muted hover:text-nord-4">
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              onClick={() => inviteMutation.mutate({ partnerOrgId, ...permissions })}
              disabled={inviteMutation.isPending || !partnerOrgId}
              className="rounded-lg bg-nord-frost-1 px-4 py-2 text-sm font-semibold text-nord-0 hover:bg-nord-frost-2 disabled:opacity-50"
            >
              {inviteMutation.isPending ? t('adminUi.sending', 'Sending…') : t('adminUi.federation.sendInvite', 'Send Invite')}
            </button>
          </div>
        </div>
      )}

      {agreementsQ.isLoading && <p className="text-sm text-nord-muted">{t('common.loading', 'Loading…')}</p>}

      {/* Agreements list */}
      <div className="flex flex-col gap-2">
        {agreementsQ.data?.agreements.map((ag) => (
          <div key={ag.id} className="rounded-lg border border-nord-2 bg-nord-1 p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-nord-muted">{ag.id.slice(0, 8)}…</span>
                  <span className={`rounded px-2 py-0.5 text-xs font-semibold ${STATUS_STYLES[ag.status]}`}>
                    {ag.status}
                  </span>
                </div>
                <p className="mt-1 text-sm text-nord-4 truncate">
                  {ag.initiating_org_id} ↔ {ag.partner_org_id}
                </p>
                <p className="mt-0.5 text-xs text-nord-muted">
                  {t('adminUi.federation.since', 'Since')}: {new Date(ag.initiated_at).toLocaleDateString(uiLang(i18n))}
                  {ag.max_classification !== 'UNCLASSIFIED' && (
                    <> · {t('adminUi.federation.max', 'Max')}: <span className="font-semibold">{ag.max_classification}</span></>
                  )}
                </p>

                {/* Permissions toggles (active only) */}
                {ag.status === 'ACTIVE' && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {[
                      ['allow_rooms',     t('adminUi.federation.permRooms', 'Rooms')],
                      ['allow_discovery', t('adminUi.federation.permDiscovery', 'Discovery')],
                      ['allow_files',     t('adminUi.federation.permFiles', 'Files')],
                      ['allow_calls',     t('adminUi.federation.permCalls', 'Calls')],
                    ].map(([key, label]) => (
                      <button
                        key={key}
                        onClick={() => togglePermission(ag.id, key, ag[key as keyof FederationAgreement] as boolean)}
                        className={[
                          'rounded px-2 py-0.5 text-xs font-medium border transition-colors',
                          (ag[key as keyof FederationAgreement] as boolean)
                            ? 'border-nord-14/60 bg-nord-14/20 text-nord-14'
                            : 'border-nord-2 text-nord-muted',
                        ].join(' ')}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-2 shrink-0">
                {ag.status === 'PENDING' && (
                  <>
                    <button
                      onClick={() => acceptMutation.mutate(ag.id)}
                      disabled={acceptMutation.isPending}
                      className="rounded-lg bg-nord-14/20 px-3 py-1.5 text-xs font-semibold text-nord-14 hover:bg-nord-14/40"
                    >
                      {t('adminUi.federation.accept', 'Accept')}
                    </button>
                    <button
                      onClick={() => declineMutation.mutate(ag.id)}
                      disabled={declineMutation.isPending}
                      className="rounded-lg border border-nord-11 px-3 py-1.5 text-xs font-semibold text-nord-11 hover:bg-nord-11/20"
                    >
                      {t('adminUi.federation.decline', 'Decline')}
                    </button>
                  </>
                )}
                {ag.status === 'ACTIVE' && (
                  <button
                    onClick={() => terminateMutation.mutate(ag.id)}
                    disabled={terminateMutation.isPending}
                    className="rounded-lg border border-nord-11 px-3 py-1.5 text-xs font-semibold text-nord-11 hover:bg-nord-11/20"
                  >
                    {t('adminUi.federation.terminate', 'Terminate')}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
